import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import { absMediaUrl } from "../utils/mediaUrl";
import { withQuietRetry } from "../utils/adminRetry";
import AdminLoadFailed from "./AdminLoadFailed";
import { Feedback, FeedbackBoardState } from "../types/admin";
import "./AdminFeedbackTab.css";

// "v1.1 (34) · ios · iPhone15,3" — whichever pieces the record carries.
function fmtContext(f: Feedback): string {
  const version = f.appVersion ? `v${f.appVersion}${f.appBuild ? ` (${f.appBuild})` : ""}` : null;
  return [version, f.platform, f.deviceModel].filter(Boolean).join(" · ");
}

function fmtDate(d?: string): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

function fmtTime(d?: string): string {
  if (!d) return "";
  try {
    return new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// Feedback board (founder, 1.5.1 #10: "a way for me to clear them"). The chips
// say where a post stands; one tap each way: Clear takes it off the board, Put
// back returns it for good (the word filter's hold lifted, reports no longer
// count). A frozen talent's posts stay off until they're unfrozen.
function BoardCell({ f, busy, onSet }: { f: Feedback; busy: boolean; onSet: (cleared: boolean) => void }) {
  if (!f.board) return <span className="muted-text">Private</span>;
  const votes = f.votes || 0;
  const reports = f.reportCount || 0;
  const frozen = f.dj?.isFrozen === true;
  return (
    <>
      <div className="action-btn-group">
        {f.onBoard && !frozen && <span className="pill adm-pill-sm adm-pill-green">On board</span>}
        {frozen && <span className="pill adm-pill-sm adm-pill-grey">Talent frozen</span>}
        {f.held && <span className="pill adm-pill-sm adm-pill-amber">Held</span>}
        {f.clearedAt && <span className="pill adm-pill-sm adm-pill-grey">Cleared</span>}
        {reports > 0 && <span className="pill adm-pill-sm adm-pill-red">Reported ×{reports}</span>}
      </div>
      <div className="muted-text adm-fs-72 adm-mt-4">{votes} {votes === 1 ? "vote" : "votes"}</div>
      <div className="action-btn-group adm-mt-6">
        {!f.clearedAt && (
          <button type="button" className="adm-btn-sm adm-btn-red-sm" disabled={busy} onClick={() => onSet(true)}>
            Clear
          </button>
        )}
        {!f.onBoard && (
          <button type="button" className="adm-btn-sm adm-btn-green-sm" disabled={busy} onClick={() => onSet(false)}>
            Put back
          </button>
        )}
      </div>
    </>
  );
}

export default function AdminFeedbackTab() {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    withQuietRetry(() => adminApi.get("/feedback"))
      .then((r) => setFeedback(Array.isArray(r.data) ? r.data : r.data?.items || []))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return feedback;
    return feedback.filter((f) => {
      const name = (f.djName || f.dj?.name || "").toLowerCase();
      return (
        name.includes(q) ||
        (f.subject || "").toLowerCase().includes(q) ||
        (f.message || "").toLowerCase().includes(q)
      );
    });
  }, [feedback, query]);

  const setCleared = async (f: Feedback, cleared: boolean) => {
    setBusyId(f._id);
    try {
      const { data } = await adminApi.patch<FeedbackBoardState>(`/feedback/${f._id}`, { cleared });
      const { held, clearedAt, kept, votes, reportCount, onBoard } = data;
      setFeedback((list) => list.map((x) => (x._id === f._id ? { ...x, held, clearedAt, kept, votes, reportCount, onBoard } : x)));
    } catch (err: any) {
      alert(err?.response?.data?.msg || "That didn't go through. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  const weekCount = useMemo(() => {
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return feedback.filter((f) => {
      const t = new Date(f.createdAt).getTime();
      return Number.isFinite(t) && t >= weekAgo;
    }).length;
  }, [feedback]);

  if (loading) {
    return (
      <section className="fade-in">
        <p className="muted-text adm-center adm-p-40">Loading feedback…</p>
      </section>
    );
  }

  // Never "No feedback yet." (or zero counts) for a list that didn't load.
  if (failed) {
    return (
      <section className="fade-in">
        <AdminLoadFailed what="feedback" onRetry={load} />
      </section>
    );
  }

  return (
    <section className="fade-in">
      {/* Stats */}
      <div className="adm-grid-5 adm-mb-14">
        {[
          { label: "Total Feedback", value: String(feedback.length), color: "" },
          { label: "Last 7 Days", value: String(weekCount), color: "var(--adm-green)" },
        ].map((s) => (
          <div key={s.label} className="kpi-card kpi-card-compact">
            <div>
              <div className="kpi-label">{s.label}</div>
              <div className="kpi-value" style={s.color ? { color: s.color } : {}}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search toolbar */}
      <div className="adm-toolbar adm-mb-14">
        <div className="adm-toolbar-group">
          <input
            className="adm-select"
            style={{ minWidth: 220 }}
            placeholder="Search talent, subject, or message…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <span className="muted-text adm-fs-76">
          {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
        </span>
      </div>

      {/* Table */}
      <div className="adm-card">
        {feedback.length === 0 ? (
          <p className="muted-text adm-center adm-p-30">
            No feedback yet.
          </p>
        ) : filtered.length === 0 ? (
          <p className="muted-text adm-center adm-p-30">
            No feedback matches “{query}”.
          </p>
        ) : (
          <div className="adm-scrollable-full adm-table-viewport">
            <table className="adm-table-light adm-table-compact sticky-header">
              <thead>
                <tr>
                  <th className="adm-nowrap">Date</th>
                  <th>Talent</th>
                  <th>Subject</th>
                  <th>Message</th>
                  <th>Board</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => {
                  const dj = f.dj;
                  const name = f.djName || dj?.name || "Unknown";
                  const avatarSrc = absMediaUrl(dj?.avatarUrl);
                  return (
                    <tr key={f._id} className="hover-row">
                      <td className="adm-nowrap adm-fs-78">
                        {fmtDate(f.createdAt)}{" "}
                        <span className="muted-text">{fmtTime(f.createdAt)}</span>
                      </td>
                      <td>
                        {dj ? (
                          <div className="dj-cell dj-cell-compact clickable" onClick={() => navigate(`/admin/dj/${dj._id}`)}>
                            {avatarSrc ? (
                              <img src={avatarSrc} className="dj-cell-avatar-sm" alt="" />
                            ) : (
                              <div className="dj-cell-placeholder-sm">{name?.[0]?.toUpperCase() || "?"}</div>
                            )}
                            <span className="dj-link-text adm-fs-82">{name}</span>
                          </div>
                        ) : (
                          <span className="adm-fs-82">
                            {name}
                            {f.djEmail && (
                              <div className="muted-text adm-fs-70">{f.djEmail}</div>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="adm-fs-82 adm-semibold" style={{ verticalAlign: "top", maxWidth: 180 }}>
                        {f.subject}
                      </td>
                      <td className="adm-fs-80" style={{ verticalAlign: "top", whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: 420, lineHeight: 1.45 }}>
                        {f.message}
                        {Array.isArray(f.photos) && f.photos.length > 0 && (
                          <div className="adm-mt-6" style={{ display: "flex", gap: 6 }}>
                            {f.photos.map((url) => (
                              <a key={url} href={url} target="_blank" rel="noreferrer">
                                <img
                                  src={url}
                                  alt="attached screenshot"
                                  className="adm-block" style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6 }}
                                />
                              </a>
                            ))}
                          </div>
                        )}
                        {fmtContext(f) && (
                          <div className="muted-text adm-mt-4" style={{ fontSize: "0.68rem" }}>
                            {fmtContext(f)}
                          </div>
                        )}
                      </td>
                      <td className="adm-fs-78 adm-fb-board">
                        <BoardCell f={f} busy={busyId === f._id} onSet={(cleared) => setCleared(f, cleared)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {feedback.some((f) => f.board) && (
        <p className="muted-text adm-fs-72">
          Board posts: Held = the word filter kept it off. Two reports take a post off and email you; Put back keeps it on. A report also hides that author from the reporter.
        </p>
      )}
    </section>
  );
}
